import io from 'socket.io-client';

let socket = null;
let currentToken = null;

// Fallback notification via service worker for devices WITHOUT a push
// subscription. When a subscription exists the server's web-push already
// shows the same notification, so showing it here too would duplicate it.
const showNotification = async (data) => {
  if (!('serviceWorker' in navigator) || !('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const pushSub = await registration.pushManager?.getSubscription();
    if (pushSub) return;
    const title = data.title || 'Bigwals';
    const options = {
      body: data.body,
      icon: data.icon || '/icons/icon-192x192.png',
      badge: data.badge || '/icons/icon-72x72.png',
      tag: data.tag || (data.data?.channelId ? `message-${data.data.channelId}` : `notif-${Date.now()}`),
      renotify: true,
      data: { url: data.data?.url || '/dashboard', ...data.data },
      vibrate: [200, 100, 200]
    };
    registration.showNotification(title, options);
  } catch (e) {
    console.error('[NOTIF] Failed to show notification:', e);
  }
};

export const initSocket = (token) => {
  // Re-fetching the current user (e.g. Sidebar refreshing the profile on
  // mount) produces a new `user` object reference, which used to retrigger
  // socket initialization even though the token hadn't changed. That tore
  // down the live connection - and with it every `socket.on(...)` listener
  // registered by Dashboard/VideoCall - out from under any active call or
  // chat session. Initialization is idempotent per token: if a socket for
  // this exact token already exists, reuse it instead of reconnecting.
  if (socket && currentToken === token) {
    return socket;
  }

  if (socket) {
    socket.disconnect();
  }

  currentToken = token;
  socket = io(process.env.REACT_APP_SOCKET_URL || 'https://management.bigwalstudio.com', {
    auth: {
      token
    },
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionAttempts: 5
  });

  socket.on('connect', () => {
    console.log('[SOCKET] Connected:', socket.id);
  });

  socket.on('disconnect', (reason) => {
    console.log('[SOCKET] Disconnected:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('[SOCKET] Connection error:', error.message);
  });

  // Show foreground push notifications
  socket.on('notification:new', (data) => {
    showNotification(data);
  });

  return socket;
};

export const getSocket = () => {
  return socket;
};

export const disconnectSocket = () => {
  currentToken = null;
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

const socketService = {
  initSocket,
  getSocket,
  disconnectSocket
};

export default socketService;
